// My Attendance — WP plugin parity (Screen E2).
// Month picker + Load + Export Excel. 7-col table: Date / Status / Check In /
// Check Out / IP / WiFi / Note.

import React, { useEffect, useState } from "react";
import { useToast } from "@chakra-ui/react";
import {
  FiCheckCircle, FiClock, FiHome, FiXCircle, FiCalendar,
  FiCoffee, FiPauseCircle, FiWifi, FiWifiOff, FiLogIn, FiLogOut,
} from "react-icons/fi";
import { api, API_BASE } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, ATTENDANCE_TONES } from "../../Components/ui";

// Each attendance status gets a colour + icon. Used by <StatusBadge />.
// Synthetic keys used only by the day-grid renderer:
//   "not_checked_in" → today, no check-in yet (neutral grey, not red)
//   "leave:<type>"   → on approved leave; shows "On Sick Leave" etc.
//
// Labels and icons stay here; colour comes from the shared tone table so these
// pills follow the theme (Components/ui/statusTones.js). This map was eight
// hardcoded light-theme triples, duplicated verbatim in the other attendance
// page.
const STATUS_META = {
  present:        { label: "Present",        ...ATTENDANCE_TONES.present,        Icon: FiCheckCircle },
  late:           { label: "Late",           ...ATTENDANCE_TONES.late,           Icon: FiClock },
  wfh:            { label: "WFH",            ...ATTENDANCE_TONES.wfh,            Icon: FiHome },
  absent:         { label: "Absent",         ...ATTENDANCE_TONES.absent,         Icon: FiXCircle },
  holiday:        { label: "Holiday",        ...ATTENDANCE_TONES.holiday,        Icon: FiCoffee },
  leave:          { label: "On Leave",       ...ATTENDANCE_TONES.leave,          Icon: FiCalendar },
  "half-day":     { label: "Half Day",       ...ATTENDANCE_TONES["half-day"],    Icon: FiPauseCircle },
  not_checked_in: { label: "Not checked in", ...ATTENDANCE_TONES.not_checked_in, Icon: FiClock },
};
const LEAVE_TYPE_LABEL = {
  annual: "Annual", sick: "Sick", casual: "Casual", unpaid: "Unpaid",
  marriage: "Marriage", bereavement: "Bereavement", examination: "Examination",
  paternity: "Paternity", maternity: "Maternity", half_day: "Half Day",
};
function StatusBadge({ status, leaveType }) {
  const m = STATUS_META[status] || STATUS_META.present;
  const Icon = m.Icon;
  const label = (status === "leave" && leaveType)
    ? `On ${LEAVE_TYPE_LABEL[leaveType] || leaveType} Leave`
    : m.label;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "2px 8px", borderRadius: 999,
      background: m.bg, color: m.fg, border: `1px solid ${m.border}`,
      fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
      whiteSpace: "nowrap",
    }}>
      <Icon size={13} strokeWidth={2.5} />
      {label}
    </span>
  );
}
function WifiBadge({ rec }) {
  if (!rec || !rec.checkIn) return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  if (rec.wifiVerified) {
    return (
      <span title="Checked in from office Wi-Fi" style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "2px 8px", borderRadius: 999,
        background: "var(--tone-success-bg)", color: "var(--tone-success-fg)", border: "1px solid var(--tone-success-br)",
        fontSize: 11, fontWeight: 700,
      }}>
        <FiWifi size={13} strokeWidth={2.5} /> Office
      </span>
    );
  }
  return (
    <span title="Checked in from outside office Wi-Fi" style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "2px 8px", borderRadius: 999,
      background: "var(--tone-warning-bg)", color: "var(--tone-warning-fg)", border: "1px solid var(--tone-warning-br)",
      fontSize: 11, fontWeight: 700,
    }}>
      <FiWifiOff size={13} strokeWidth={2.5} /> Off-network
    </span>
  );
}
function TimeCell({ time, kind }) {
  if (!time) return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  const Icon = kind === "out" ? FiLogOut : FiLogIn;
  const [h, m] = time.split(":").map(Number);
  const d = new Date(); d.setHours(h, m, 0, 0);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}>
      <Icon size={12} style={{ opacity: 0.55 }} />
      {d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
    </span>
  );
}

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function MyAttendancePage() {
  const toast = useToast();
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(false);

  useShellHeader({ title: "My Attendance" }, []);

  async function load(m = month) {
    setLoading(true);
    try {
      const r = await api(`/attendance/my?month=${encodeURIComponent(m)}`);
      setRows(Array.isArray(r) ? r : (r.rows || r.records || r.attendance || []));
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, []);

  function exportXlsx() {
    // WP parity: dfhr_export_my_attendance → /api/exports/my-attendance.xlsx
    const token = localStorage.getItem("token");
    const url   = `${API_BASE}/exports/my-attendance.xlsx?month=${encodeURIComponent(month)}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.blob() : Promise.reject(r))
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u; a.download = `My_Attendance_${month}.xlsx`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast({ title: "Could not export. Try again.", status: "error" }));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", margin: 0 }}>My Attendance</h2>

      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <label style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-tertiary)", fontWeight: 600 }}>Month:</label>
        <input
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          style={{ ...inputStyle, width: 180 }}
        />
        <button onClick={() => load()} style={btnPrimary} disabled={loading}>{loading ? "Loading…" : "Load"}</button>
        <button onClick={exportXlsx} style={btnGhost}>📥 Export Excel</button>
      </div>

      <Card padding="none">
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th>Date</Th><Th>Status</Th><Th>Check In</Th><Th>Check Out</Th><Th>IP</Th><Th>WiFi</Th><Th>Note</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><Td colSpan={7} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: "24px 12px" }}>No attendance records for this month.</Td></tr>
              ) : rows.map((r) => (
                <tr key={r._id || r.workDate}>
                  <Td className="tabular">{fmtDate(r.workDate)}</Td>
                  <Td><StatusBadge status={r.status} /></Td>
                  <Td><TimeCell time={r.checkIn}  kind="in"  /></Td>
                  <Td><TimeCell time={r.checkOut} kind="out" /></Td>
                  <Td style={{ fontSize: 12, color: "var(--text-tertiary)" }} className="tabular">{r.ipAddress || "—"}</Td>
                  <Td><WifiBadge rec={r} /></Td>
                  <Td style={{ fontSize: 12, color: "var(--text-tertiary)" }}>{r.note || ""}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function fmtDate(d) { try { return new Date(d).toISOString().slice(0, 10); } catch { return "—"; } }
const thStyle = {
  textAlign: "left", padding: "10px 14px", fontSize: 11, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: "0.06em",
  color: "var(--text-tertiary)", background: "var(--bg-subtle)",
  borderBottom: "1px solid var(--border-subtle)",
};
const tdBase = { padding: "10px 14px", borderTop: "1px solid var(--border-subtle)", color: "var(--text-primary)" };
const inputStyle = {
  border: "1px solid var(--border-default)", borderRadius: 8, padding: "6px 10px",
  fontSize: 13, background: "var(--bg-surface)", color: "var(--text-primary)",
};
const btnPrimary = {
  background: "var(--accent-primary)", color: "white", border: "none",
  padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
};
const btnGhost = {
  background: "transparent", color: "var(--text-secondary)",
  padding: "8px 14px", border: "1px solid var(--border-default)", borderRadius: 8,
  fontSize: 13, fontWeight: 500, cursor: "pointer",
};
function Th({ children }) { return <th style={thStyle}>{children}</th>; }
function Td({ children, style, className, colSpan }) { return <td colSpan={colSpan} style={{ ...tdBase, ...style }} className={className}>{children}</td>; }
